import type { FC } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { XCloseIcon } from '@/components/ui/icons'
import { Modal } from '@/components/ui/modal'
import {
	ModalBody,
	ModalFooter,
	ModalHeader
} from '@/components/ui/modal-atoms'
import { useAddPhotosetSpaceFlag } from '@/features/photoset/queries/use-add-photoset-space-flag'
import type { PhotosetSpace } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface CurationPhotosetSpaceRulesModalProps {
	photosetSpaceId: PhotosetSpace['id']
	spaceFlagValue: PhotosetSpace['flag_key_name']
	hideModal: () => void
}

export const CurationPhotosetSpaceRulesModal: FC<
	CurationPhotosetSpaceRulesModalProps
> = ({ photosetSpaceId, spaceFlagValue, hideModal }) => {
	const { t } = useTranslation()
	const { mutate: addPhotosetSpaceFlag } = useAddPhotosetSpaceFlag({
		onError: (error) => errorHandler(error)
	})

	return (
		<Modal onOverlayClick={() => hideModal()} width={480}>
			<ModalHeader>
				<ButtonLegacy
					onClick={() => hideModal()}
					shape='square'
					size='sm'
					type='button'
					variant='tertiary'
				>
					<XCloseIcon />
				</ButtonLegacy>
				<h3>{t('photoset:curation.rules_modal.title')}</h3>
			</ModalHeader>

			<ModalBody>
				<div className='flex flex-col gap-2'>
					<h2 className='font-bold text-neutral-900 text-xl'>
						{t('photoset:curation.rules_modal.title')}
					</h2>
					<p className='text-base text-neutral-900'>
						{t('photoset:curation.rules_modal.paragraph')}
					</p>
				</div>

				<div className='flex flex-col gap-4'>
					<button
						className={`w-full cursor-pointer rounded-xl px-4 py-3 text-left outline outline-1 ${
							spaceFlagValue !== 'sufficient_coverage'
								? 'bg-neutral-50 outline-primary-600'
								: 'bg-neutral-white outline-neutral-300'
						}`}
						onClick={() =>
							addPhotosetSpaceFlag({
								photosetSpaceId,
								flag: null
							})
						}
						type='button'
					>
						<h4 className='font-medium text-neutral-900 text-sm'>
							{t('photoset:curation.rules_modal.quality_title')}
						</h4>
						<p className='text-neutral-700 text-sm'>
							{t(
								'photoset:curation.rules_modal.quality_description'
							)}
						</p>
					</button>
					<button
						className={`w-full cursor-pointer rounded-xl px-4 py-3 text-left outline outline-1 ${
							spaceFlagValue === 'sufficient_coverage'
								? 'bg-neutral-50 outline-primary-600'
								: 'bg-neutral-white outline-neutral-300'
						}`}
						onClick={() =>
							addPhotosetSpaceFlag({
								photosetSpaceId,
								flag: 'sufficient_coverage'
							})
						}
						type='button'
					>
						<h4 className='font-medium text-neutral-900 text-sm'>
							{t('photoset:curation.rules_modal.coverage_title')}
						</h4>
						<p className='text-neutral-700 text-sm'>
							{t(
								'photoset:curation.rules_modal.coverage_description'
							)}
						</p>
					</button>
				</div>
			</ModalBody>

			<ModalFooter className='justify-center'>
				<ButtonLegacy
					onClick={() => hideModal()}
					type='button'
					variant='tertiary'
				>
					{t('common:close')}
				</ButtonLegacy>
			</ModalFooter>
		</Modal>
	)
}
